import { ApolloClient } from '@apollo/client'
import { CANCEL_PAYMENT, CANCEL_CARD_PAYMENT } from '../graphql/mutations'
import { PAYMENT_STATUS, GET_PAYMENT_NOTIFICATIONS, GET_PENDING_PAYMENTS_BY_TERMINAL } from '../graphql/queries'
import { Payment } from '../types/order'

export interface PaymentNotification {
  id: number
  payment_id: number
  type: 'TERMINAL_MESSAGE' | 'STATUS_CHANGE' | 'PAYMENT_COMPLETED' | 'terminal_message' | 'status_change' | 'payment_completed'
  message: string | null
  message_order_number: number | null
  status: string | null
  failure_code: string | null
  created_at: string
}

export class PaymentService {
  constructor(private apolloClient: ApolloClient<any>) {}

  // Note: processPayment has been removed
  // Payments are now created directly with orders via the createOrder mutation
  // The payment is included in the orderData.payment field and processed automatically:
  // - Cash payments: Immediately completed, order auto-confirmed if fully paid
  // - Card payments: Processed via CCV terminal, status updated via polling
  // - Online payments: Not yet implemented (will throw error)
  
  // getPaymentStatus is used for polling payment status
  // For card payments, poll this endpoint periodically until payment reaches a final state


  async getPaymentStatus(paymentId: number): Promise<Payment> {
    try {
      const response = await this.apolloClient.query({
        query: PAYMENT_STATUS,
        variables: {
          id: paymentId
        },
        fetchPolicy: 'network-only' // Always fetch fresh data
      })

      return response.data.paymentStatus
    } catch (error) {
      throw new Error('Failed to check payment status. Please try again.')
    }
  }

  async getPaymentNotifications(paymentId: number): Promise<PaymentNotification[]> {
    try {
      const response = await this.apolloClient.query({
        query: GET_PAYMENT_NOTIFICATIONS,
        variables: {
          paymentId
        },
        fetchPolicy: 'network-only' // Always fetch fresh data
      })

      return response.data.paymentNotifications || []
    } catch (error) {
      throw new Error('Failed to fetch payment notifications. Please try again.')
    }
  }

  async cancelPayment(terminalId?: string | null): Promise<{ success: boolean; message: string; reference?: string; payment_id?: number }> {
    // Use provided terminal_id, or fall back to hardcoded value if not provided
    const effectiveTerminalId = terminalId || 'LEEN0208'
    try {
      const response = await this.apolloClient.mutate({
        mutation: CANCEL_PAYMENT,
        variables: {
          terminal_id: effectiveTerminalId
        }
      })

      return response.data.cancelPayment
    } catch (error) {
      throw new Error('Failed to cancel payment. Please try again.')
    }
  }

  async cancelCardPaymentById(paymentId: number): Promise<{ success: boolean; message: string; reference?: string; payment_id?: number }> {
    try {
      const response = await this.apolloClient.mutate({
        mutation: CANCEL_CARD_PAYMENT,
        variables: {
          payment_id: paymentId
        }
      })

      return response.data.cancelCardPaymentById
    } catch (error) {
      throw new Error('Failed to cancel payment. Please try again.')
    }
  }

  async getPendingPaymentsByTerminal(terminalId: string): Promise<Payment[]> {
    try {
      const response = await this.apolloClient.query({
        query: GET_PENDING_PAYMENTS_BY_TERMINAL,
        variables: {
          terminal_id: terminalId
        },
        fetchPolicy: 'network-only' // Always fetch fresh data
      })

      return response.data.pendingPaymentsByTerminal || []
    } catch (error) {
      throw new Error('Failed to fetch pending payments. Please try again.')
    }
  }
}

